import React, { useMemo, useState } from "react";
import { AppShell } from "@/components/bi/AppShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BookOpen, Copy, KeySquare, ExternalLink, Terminal, Zap, PlayCircle, AlertTriangle } from "lucide-react";

// Base URL discovered at build time via CRA env — matches what the API client uses.
const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const V1 = `${BASE}/api/v1`;

const ENDPOINTS = [
  {
    id: "me",
    method: "GET", path: "/api/v1/me", scope: "read",
    title: "Whoami",
    summary: "Return the token owner and effective scopes. Useful as a first call to verify a token works.",
    curl: (t) => `curl -H "Authorization: Bearer ${t}" \\\n     ${V1}/me`,
    run: () => ({ url: `${V1}/me`, method: "GET" }),
    params: [],
  },
  {
    id: "dashboards",
    method: "GET", path: "/api/v1/dashboards", scope: "read",
    title: "List dashboards",
    summary: "Every dashboard visible to the token owner (tenant + role + RLS filtered).",
    curl: (t) => `curl -H "Authorization: Bearer ${t}" \\\n     ${V1}/dashboards`,
    run: () => ({ url: `${V1}/dashboards`, method: "GET" }),
    params: [],
  },
  {
    id: "dashboard-get",
    method: "GET", path: "/api/v1/dashboards/{id}", scope: "read",
    title: "Get a dashboard",
    summary: "Full dashboard definition (widgets, layout, canvas) — the same payload the builder loads.",
    curl: (t) => `curl -H "Authorization: Bearer ${t}" \\\n     ${V1}/dashboards/DASHBOARD_ID`,
    run: (p) => ({ url: `${V1}/dashboards/${encodeURIComponent(p.dashboardId)}`, method: "GET" }),
    params: [{ key: "dashboardId", label: "Dashboard ID", placeholder: "dsh_…" }],
  },
  {
    id: "datasets",
    method: "GET", path: "/api/v1/datasets", scope: "read",
    title: "List datasets",
    summary: "Datasets you can query with this token, plus their column names.",
    curl: (t) => `curl -H "Authorization: Bearer ${t}" \\\n     ${V1}/datasets`,
    run: () => ({ url: `${V1}/datasets`, method: "GET" }),
    params: [],
  },
  {
    id: "query",
    method: "POST", path: "/api/v1/datasets/{id}/query", scope: "query",
    title: "Run a semantic query",
    summary: "Execute an ad-hoc dimension + measure query against a dataset. Row-level security is applied. Field names must come from the dataset schema — call GET /datasets first to discover them.",
    curl: (t) =>
`# Replace the field names with real columns from GET /datasets first.
curl -X POST -H "Authorization: Bearer ${t}" \\
     -H "Content-Type: application/json" \\
     -d '{
       "dimensions": [{"field": "YOUR_DIMENSION"}],
       "measures":   [{"field": "YOUR_METRIC", "agg": "sum"}],
       "filters":    [],
       "limit": 100
     }' \\
     ${V1}/datasets/DATASET_ID/query`,
    run: (p) => ({
      url: `${V1}/datasets/${encodeURIComponent(p.datasetId)}/query`,
      method: "POST",
      body: p.body || '{"dimensions":[{"field":"YOUR_DIMENSION"}],"measures":[{"field":"YOUR_METRIC","agg":"sum"}],"filters":[],"limit":10}',
    }),
    params: [
      { key: "datasetId", label: "Dataset ID (from GET /datasets)", placeholder: "ds_…" },
      { key: "body", label: "JSON body — replace YOUR_DIMENSION / YOUR_METRIC with real column names", textarea: true,
        defaultValue: '{"dimensions":[{"field":"YOUR_DIMENSION"}],"measures":[{"field":"YOUR_METRIC","agg":"sum"}],"filters":[],"limit":10}' },
    ],
  },
  {
    id: "reports",
    method: "GET", path: "/api/v1/reports", scope: "read",
    title: "List reports",
    summary: "Enterprise Report Maker designs available to the token owner.",
    curl: (t) => `curl -H "Authorization: Bearer ${t}" \\\n     ${V1}/reports`,
    run: () => ({ url: `${V1}/reports`, method: "GET" }),
    params: [],
  },
  {
    id: "render",
    method: "POST", path: "/api/v1/reports/{id}/render?fmt=pdf", scope: "render",
    title: "Render a report",
    summary: "Render a report design in pdf / xlsx / docx / html and download the file. Supply parameters in the JSON body.",
    curl: (t) =>
`curl -X POST -H "Authorization: Bearer ${t}" \\
     -H "Content-Type: application/json" \\
     -d '{ "params": { "Region": "EMEA" } }' \\
     "${V1}/reports/REPORT_ID/render?fmt=pdf" \\
     -o report.pdf`,
    run: (p) => ({
      url: `${V1}/reports/${encodeURIComponent(p.reportId)}/render?fmt=${encodeURIComponent(p.fmt || "html")}`,
      method: "POST",
      body: p.body || '{"params":{}}',
      binary: (p.fmt || "html") !== "html",
    }),
    params: [
      { key: "reportId", label: "Report ID", placeholder: "rpt_…" },
      { key: "fmt", label: "Format (pdf/xlsx/docx/html)", placeholder: "html", defaultValue: "html" },
      { key: "body", label: "JSON body", placeholder: '{"params":{}}', textarea: true, defaultValue: '{"params":{}}' },
    ],
  },
];

const RATE_LIMITS = [
  { scope: "Per token", limit: "600 requests / minute", note: "Sliding-window guard applied on the ingress. Bursts up to 60 req/s are OK." },
  { scope: "Query payload", limit: "10,000 rows max", note: "Larger payloads should be paginated or exported through scheduled API reports." },
  { scope: "Render endpoint", limit: "60 renders / hour / token", note: "PDF/XLSX renders are CPU-heavy; contact your workspace admin for higher limits." },
  { scope: "Concurrency", limit: "4 in-flight requests / token", note: "Additional requests queue for up to 30s before returning 429." },
];

const ERROR_CODES = [
  { code: 200, meaning: "OK", note: "Request succeeded." },
  { code: 400, meaning: "Bad Request", note: "Validation failed — read the `detail` field for specifics." },
  { code: 401, meaning: "Unauthorized", note: "Token missing, malformed, revoked, or its owner deleted." },
  { code: 403, meaning: "Forbidden", note: "Token exists but lacks the required scope (read/query/render)." },
  { code: 404, meaning: "Not Found", note: "Resource does not exist, or RLS filters it out for this token owner." },
  { code: 410, meaning: "Gone", note: "Shared link expired." },
  { code: 429, meaning: "Too Many Requests", note: "Rate limit exceeded — back off (see `Retry-After` header)." },
  { code: 500, meaning: "Server Error", note: "Unexpected server failure. Retry with exponential backoff." },
];

const METHOD_COLORS = {
  GET: "text-emerald-700 bg-emerald-50 border-emerald-200",
  POST: "text-sky-700 bg-sky-50 border-sky-200",
};

export default function ApiDocs() {
  const [token, setToken] = useState("eat_YOUR_TOKEN_HERE");

  const copy = async (v) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(v);
      } else {
        const ta = document.createElement("textarea");
        ta.value = v; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      }
      toast.success("Copied");
    } catch (_e) {
      toast.error("Copy blocked — select the snippet and press ⌘/Ctrl-C");
    }
  };

  const stats = useMemo(() => ({
    endpoints: ENDPOINTS.length,
    scopes: Array.from(new Set(ENDPOINTS.map((e) => e.scope))),
  }), []);

  return (
    <AppShell title="API documentation">
      <div className="px-5 md:px-8 py-6 max-w-4xl mx-auto space-y-5">
        <Card className="glass-card border-0 p-6" data-testid="api-docs-intro">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[color:var(--brand-ocean)]" />
            <h2 className="font-display text-xl text-[color:var(--ink)]">Public REST API — <code className="text-sm">/api/v1</code></h2>
          </div>
          <p className="text-sm text-[color:var(--ink-3)] mt-2 leading-relaxed">
            {stats.endpoints} endpoints across {stats.scopes.length} scopes ({stats.scopes.join(" · ")}).
            Every call runs with the token owner&apos;s permissions — tenant isolation, dashboard permissions and
            row-level security are enforced server-side. Create a token from{" "}
            <a href="/settings" className="text-[color:var(--brand-ocean)] underline">Settings → API tokens</a>.
          </p>
          <div className="mt-4 bg-[color:var(--surface-2)] rounded-xl border border-[color:var(--glass-border)] p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-[color:var(--ink-2)]">
              <KeySquare className="w-4 h-4" /> Base URL &amp; authentication
            </div>
            <div className="mt-2 space-y-1.5 text-[12px]">
              <div className="flex items-center gap-2"><span className="text-[color:var(--ink-3)] w-24">Base URL</span>
                <code data-testid="api-docs-base-url" className="text-[color:var(--ink)] flex-1 truncate">{V1}</code>
                <Button size="sm" variant="ghost" onClick={() => copy(V1)} className="h-6 px-2"><Copy className="w-3 h-3" /></Button>
              </div>
              <div className="flex items-center gap-2"><span className="text-[color:var(--ink-3)] w-24">Auth header</span>
                <code className="text-[color:var(--ink)] flex-1 truncate">Authorization: Bearer eat_...</code>
              </div>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-semibold text-[color:var(--ink-2)] mb-1">
              Insert your token to personalize every example below
            </label>
            <input
              data-testid="api-docs-token-input"
              value={token}
              onChange={(e) => setToken(e.target.value.trim() || "eat_YOUR_TOKEN_HERE")}
              spellCheck={false}
              className="w-full font-mono text-[12px] bg-[color:var(--surface)] border border-[color:var(--glass-border)] rounded-lg px-3 py-2 text-[color:var(--ink)]"
              placeholder="eat_..."
            />
            <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Tokens are shown only once at creation time. Nothing you type here is sent — it only substitutes into the curl snippets on this page.</p>
          </div>
        </Card>

        {ENDPOINTS.map((e) => {
          const snippet = e.curl(token);
          return (
            <Card key={e.id} className="glass-card border-0 p-5" data-testid={`api-docs-endpoint-${e.id}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span data-testid={`api-docs-method-${e.id}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 border rounded ${METHOD_COLORS[e.method] || "text-[color:var(--ink-2)]"}`}>{e.method}</span>
                <code className="text-[13px] text-[color:var(--ink)] font-medium">{e.path}</code>
                <span className="text-[10px] uppercase font-semibold text-[color:var(--brand-ocean)] ml-auto">scope: {e.scope}</span>
              </div>
              <div className="mt-3">
                <h3 className="font-display text-base text-[color:var(--ink)]">{e.title}</h3>
                <p className="text-sm text-[color:var(--ink-3)] mt-1">{e.summary}</p>
              </div>
              <div className="mt-3 rounded-xl bg-[#0F172A] text-[#E2E8F0] p-3 pr-14 relative overflow-x-auto">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[#94A3B8] mb-1">
                  <Terminal className="w-3 h-3" /> curl
                </div>
                <pre data-testid={`api-docs-curl-${e.id}`} className="text-[12px] font-mono leading-relaxed whitespace-pre-wrap break-all">{snippet}</pre>
                <button data-testid={`api-docs-copy-${e.id}`} onClick={() => copy(snippet)}
                  className="absolute top-2 right-2 h-7 px-2 rounded-md bg-[#1E293B] hover:bg-[#334155] text-[#E2E8F0] flex items-center gap-1 text-[11px]">
                  <Copy className="w-3 h-3" /> Copy
                </button>
              </div>
              <TryItPanel endpoint={e} token={token} />
            </Card>
          );
        })}

        <Card className="glass-card border-0 p-5" data-testid="api-docs-rate-limits">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-[color:var(--brand-mint)]" />
            <h3 className="font-display text-lg text-[color:var(--ink)]">Rate limits</h3>
          </div>
          <p className="text-sm text-[color:var(--ink-3)] mt-1">Every response includes <code className="text-xs">X-RateLimit-Remaining</code> and <code className="text-xs">Retry-After</code> headers when applicable.</p>
          <div className="mt-3 rounded-xl border border-[color:var(--glass-border)] overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[color:var(--surface-2)] text-[color:var(--ink-3)]"><tr>
                <th className="text-left px-3 py-2 font-semibold">Scope</th>
                <th className="text-left px-3 py-2 font-semibold">Limit</th>
                <th className="text-left px-3 py-2 font-semibold">Notes</th>
              </tr></thead>
              <tbody>
                {RATE_LIMITS.map((r, i) => (
                  <tr key={i} data-testid={`api-docs-rate-row-${i}`} className="border-t border-[color:var(--glass-border)]">
                    <td className="px-3 py-2 font-medium text-[color:var(--ink)]">{r.scope}</td>
                    <td className="px-3 py-2 text-[color:var(--brand-ocean)] font-mono">{r.limit}</td>
                    <td className="px-3 py-2 text-[color:var(--ink-3)]">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="glass-card border-0 p-5" data-testid="api-docs-errors">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[color:var(--brand-coral)]" />
            <h3 className="font-display text-lg text-[color:var(--ink)]">Error codes</h3>
          </div>
          <p className="text-sm text-[color:var(--ink-3)] mt-1">Non-2xx responses always include a JSON body with a human-readable <code className="text-xs">detail</code> field.</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {ERROR_CODES.map((e) => (
              <div key={e.code} data-testid={`api-docs-error-${e.code}`} className="rounded-lg bg-[color:var(--surface-2)] px-3 py-2 text-xs">
                <div className="flex items-baseline gap-2">
                  <code className={`font-mono font-bold ${e.code >= 500 ? "text-[color:var(--danger)]" : e.code >= 400 ? "text-amber-600" : "text-emerald-600"}`}>{e.code}</code>
                  <span className="font-medium text-[color:var(--ink)]">{e.meaning}</span>
                </div>
                <div className="text-[color:var(--ink-3)] mt-0.5">{e.note}</div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="glass-card border-0 p-5" data-testid="api-docs-help">
          <div className="flex items-start gap-3">
            <ExternalLink className="w-4 h-4 text-[color:var(--brand-ocean)] mt-0.5" />
            <div className="text-sm text-[color:var(--ink-2)]">
              <div className="font-medium text-[color:var(--ink)]">Need higher rate limits, streaming or webhooks?</div>
              <p className="text-[color:var(--ink-3)] text-[13px] mt-1">
                The v1 surface is designed for programmatic dashboards, embeds and scheduled data pulls.
                Contact your workspace admin for tenant-wide keys, IP allowlisting or event webhook delivery.
                Or wire up <a href="/settings" className="text-[color:var(--brand-ocean)] underline">Scheduled API reports</a> to push CSV/JSON to a partner endpoint automatically.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </AppShell>
  );
}

function TryItPanel({ endpoint, token }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(() => {
    const seed = {};
    for (const p of endpoint.params) if (p.defaultValue !== undefined) seed[p.key] = p.defaultValue;
    return seed;
  });
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState(null);
  const [schemaHint, setSchemaHint] = useState(null);
  const usableToken = token && token.startsWith("eat_") && token !== "eat_YOUR_TOKEN_HERE";

  // Auto-schema: when the /query endpoint's Dataset ID field changes, fetch its columns
  // and pre-fill the JSON body with real field names from the dataset.
  React.useEffect(() => {
    if (endpoint.id !== "query" || !usableToken) return;
    const dsId = (values.datasetId || "").trim();
    if (!dsId) { setSchemaHint(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await fetch(`${V1}/datasets`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const list = await r.json();
        const ds = (Array.isArray(list) ? list : []).find((d) => d.id === dsId);
        if (!ds || cancelled) return;
        // Columns may be an array of strings (older shape) or {name,type} objects.
        const rawCols = ds.columns || [];
        const cols = rawCols.map((c) => (typeof c === "string" ? { name: c, type: "string" } : c)).filter((c) => c && c.name);
        if (cols.length === 0) return;
        // Pick a text-ish dimension and a numeric-ish measure heuristically.
        const numericTypes = ["int", "integer", "number", "float", "double", "decimal", "long", "numeric"];
        const measureCol = cols.find((c) => numericTypes.includes((c.type || "").toLowerCase())) || cols[cols.length - 1];
        const dimCol = cols.find((c) => c !== measureCol && !numericTypes.includes((c.type || "").toLowerCase())) || cols[0];
        setSchemaHint({ dataset: ds.name, dim: dimCol.name, measure: measureCol.name, total: cols.length });
        // Only overwrite the body if it still looks like the placeholder.
        setValues((cur) => {
          const body = cur.body || "";
          const looksLikeDefault = !body || body.includes("YOUR_DIMENSION") || body.includes("YOUR_METRIC");
          if (!looksLikeDefault) return cur;
          return {
            ...cur,
            body: JSON.stringify({
              dimensions: [{ field: dimCol.name }],
              measures: [{ field: measureCol.name, agg: "sum" }],
              filters: [],
              limit: 10,
            }, null, 2),
          };
        });
      } catch (_e) { /* silent */ }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint.id, values.datasetId, token, usableToken]);

  const run = async () => {
    if (!usableToken) { setResp({ error: "Paste a real token above (starts with eat_) to try this endpoint." }); return; }
    for (const p of endpoint.params) {
      if (!p.textarea && !values[p.key]) { setResp({ error: `Fill in "${p.label}" first.` }); return; }
    }
    setBusy(true); setResp(null);
    try {
      const spec = endpoint.run(values || {});
      const opts = { method: spec.method, headers: { Authorization: `Bearer ${token}` } };
      if (spec.body) { opts.headers["Content-Type"] = "application/json"; opts.body = spec.body; }
      const r = await fetch(spec.url, opts);
      const status = r.status;
      if (spec.binary && r.ok) {
        const blob = await r.blob();
        setResp({ status, ok: r.ok, binary: true, size: blob.size, contentType: r.headers.get("content-type") });
      } else {
        const text = await r.text();
        let parsed = text;
        try { parsed = JSON.stringify(JSON.parse(text), null, 2); } catch { /* keep raw */ }
        setResp({ status, ok: r.ok, body: parsed.slice(0, 6000) + (parsed.length > 6000 ? "\n… truncated" : "") });
      }
    } catch (e) {
      setResp({ error: `Network error: ${e?.message || e}` });
    }
    setBusy(false);
  };

  return (
    <div className="mt-3">
      <button data-testid={`api-docs-tryit-toggle-${endpoint.id}`} onClick={() => setOpen((v) => !v)}
        className="text-[11px] flex items-center gap-1 text-[color:var(--brand-ocean)] hover:underline">
        <PlayCircle className="w-3.5 h-3.5" />{open ? "Hide" : "Try it now"}
      </button>
      {open && (
        <div data-testid={`api-docs-tryit-${endpoint.id}`} className="mt-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3 space-y-2">
          {endpoint.params.length === 0 && (
            <div className="text-[11px] text-[color:var(--ink-3)]">No parameters. Click Run to send.</div>
          )}
          {schemaHint && (
            <div data-testid={`api-docs-tryit-schema-${endpoint.id}`} className="text-[11px] rounded-lg bg-[color:rgba(43,78,255,0.08)] border border-[color:var(--brand-ocean)]/25 px-2 py-1.5 text-[color:var(--brand-ocean)]">
              <span className="font-semibold">Auto-schema:</span> {schemaHint.dataset} has {schemaHint.total} columns — prefilled with <code className="text-[color:var(--ink)]">{schemaHint.dim}</code> · <code className="text-[color:var(--ink)]">{schemaHint.measure}</code>. Edit freely.
            </div>
          )}
          {endpoint.params.map((p) => (
            <div key={p.key}>
              <label className="text-[11px] font-semibold text-[color:var(--ink-2)]">{p.label}</label>
              {p.textarea ? (
                <textarea data-testid={`api-docs-tryit-${endpoint.id}-${p.key}`} rows={4} value={values[p.key] || ""} onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
                  placeholder={p.placeholder} className="w-full font-mono text-[11px] mt-1 bg-[color:var(--surface)] border border-[color:var(--glass-border)] rounded-lg px-2 py-1.5 text-[color:var(--ink)]" />
              ) : (
                <input data-testid={`api-docs-tryit-${endpoint.id}-${p.key}`} value={values[p.key] || ""} onChange={(e) => setValues({ ...values, [p.key]: e.target.value })}
                  placeholder={p.placeholder} className="w-full font-mono text-[11px] mt-1 bg-[color:var(--surface)] border border-[color:var(--glass-border)] rounded-lg px-2 py-1.5 text-[color:var(--ink)]" />
              )}
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Button data-testid={`api-docs-tryit-run-${endpoint.id}`} onClick={run} disabled={busy} size="sm" className="bg-[color:var(--brand-ocean)] text-white">
              <PlayCircle className="w-3.5 h-3.5 mr-1" />{busy ? "Running…" : "Run"}
            </Button>
            {!usableToken && <span className="text-[10px] text-amber-600">Paste your token above first</span>}
          </div>
          {resp && (
            <div data-testid={`api-docs-tryit-response-${endpoint.id}`} className="mt-2">
              {resp.error ? (
                <div className="text-[11px] text-[color:var(--danger)]">{resp.error}</div>
              ) : (
                <>
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--ink-3)] flex items-center gap-2">
                    <span data-testid={`api-docs-tryit-status-${endpoint.id}`} className={resp.ok ? "text-emerald-600 font-bold" : "text-[color:var(--danger)] font-bold"}>Status {resp.status}</span>
                    {resp.contentType && <span className="text-[color:var(--ink-3)]">{resp.contentType}</span>}
                    {resp.size !== undefined && <span className="text-[color:var(--ink-3)]">{resp.size} bytes</span>}
                  </div>
                  {resp.binary ? (
                    <div className="text-[11px] text-[color:var(--ink-3)] mt-1 italic">Binary file received ({resp.size} bytes). Use the curl example with <code>-o output.pdf</code> to save it locally.</div>
                  ) : (
                    <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-[#0F172A] text-[#E2E8F0] p-2 text-[11px] font-mono whitespace-pre-wrap break-all">{resp.body}</pre>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
